import * as devalue from 'devalue';
import { acceptHMRUpdate, defineStore } from 'pinia';

import { computed, ref, storeToRefs, useProviderConfig } from '#imports';
import { useTransactionStore } from '@/stores/transaction/useTransactionStore';

export enum CartStateEnum {
    CART,
    PAYMENT,
    TRANSACTIONS,
    SUCCESS
}

export const useCartStore = defineStore(
    'cart',
    () => {
        const cart = ref<Map<AppCartItemType['uuid'], AppCartItemType>>(new Map());

        const cartValues = computed(() => {
            const expired: AppCartItemType[] = [];
            const active: AppCartItemType[] = [];

            for (const item of cart.value.values()) {
                if (item.status === 'expired') expired.push(item);
                else active.push(item);
            }

            return { expired, active };
        });

        const transactionStore = useTransactionStore();

        const { utils: { isEvmErc1155 } } = useProviderConfig()(['utils']);

        const { transactions } = storeToRefs(transactionStore);

        const networkDifferences = ref<{
            showModal: boolean;
            item: Nullable<AppCartItemType>;
        }>({
            showModal: false,
            item: null
        });

        const cartState = ref<{
            visible: boolean;
            state: CartStateEnum;
        }>({
            visible: false,
            state: CartStateEnum.CART
        });

        const setCartState = ({ visible, state }: {
            visible?: boolean;
            state?: CartStateEnum;
        }) => {
            cartState.value = {
                state: state ?? cartState.value.state,
                visible: visible ?? cartState.value.visible
            };
        };

        const totalSumm = computed(() => {
            const result = cartValues.value.active.reduce(
                (acc, nft) => {
                    if (nft.price) {
                        const { amountUsd, symbol, amount } = nft.price;

                        if (isEvmErc1155(nft.itemType) && nft?.amount) {
                            acc.totalAmountUsd += amountUsd * nft.amount;

                            if (!acc.tokens[symbol]) {
                                acc.tokens[symbol] = { totalAmount: 0 };
                            }
                            acc.tokens[symbol].totalAmount += amount * nft.amount;
                        }
                        else {
                            acc.totalAmountUsd += amountUsd;

                            if (!acc.tokens[symbol]) {
                                acc.tokens[symbol] = { totalAmount: 0 };
                            }
                            acc.tokens[symbol].totalAmount += amount;
                        }
                    }
                    return acc;
                },
                {
                    totalAmountUsd: 0,
                    tokens: {} as Record<string, { totalAmount: number }>
                }
            );

            const tokens = Object.keys(result.tokens).map(symbol => ({
                name: symbol,
                totalAmount: result.tokens[symbol]
                    ? parseFloat(result.tokens[symbol].totalAmount.toFixed(5))
                    : 0
            }));

            result.totalAmountUsd = parseFloat(result.totalAmountUsd.toFixed(2));

            return {
                totalAmountUsd: result.totalAmountUsd,
                tokens
            };
        });

        const changeCartItem = (uuid: AppCartItemType['uuid'], changes: Partial<AppCartItemType>) => {
            const cartItem = cart.value.get(uuid);
            if (cartItem) {
                cart.value.set(
                    uuid,
                    {
                        ...cartItem,
                        ...changes
                    }
                );
            }
        };

        const addToCart = (elem: AppCartItemType) => {
            const values = Array.from(cart.value.values());
            if (cartState.value.state === CartStateEnum.SUCCESS && !!transactions.value.length) {
                transactionStore.clearTransactions();
                setCartState({ state: CartStateEnum.CART });
            }
            if (!values.length || values.some(item => item.network === elem.network)) {
                cart.value.set(
                    elem.uuid,
                    {
                        ...elem,
                        ...isEvmErc1155(elem.itemType) && !elem.amount
                            ? { amount: 1 }
                            : {}
                    }
                );
            }
            else {
                networkDifferences.value = {
                    showModal: true,
                    item: elem
                };
            }
        };

        const clearCart = () => cart.value.clear();

        const addNetworkDifferenceAnyWay = () => {
            if (networkDifferences.value.item) {
                clearCart();
                cart.value.set(
                    networkDifferences.value.item.uuid,
                    {
                        ...networkDifferences.value.item,
                        ...isEvmErc1155(networkDifferences.value.item.itemType)
                            ? { amount: 1 }
                            : {}
                    }
                );
                networkDifferences.value = {
                    showModal: false,
                    item: null
                };
            }
        };

        const setShowModalNetworkDifferences = (val: boolean) => {
            networkDifferences.value = {
                ...networkDifferences.value,
                showModal: val
            };
        };

        const removeFromCart = (uuid: AppCartItemType['uuid']) => {
            cart.value.delete(uuid);
        };

        const handleCartItems = (item: AppCartItemType) => {
            if (cart.value.has(item.uuid)) {
                removeFromCart(item.uuid);
            }
            else {
                addToCart(item);
            }
        };

        return {
            cart,
            cartValues,
            addToCart,
            removeFromCart,
            clearCart,
            handleCartItems,
            totalSumm,
            networkDifferences,
            addNetworkDifferenceAnyWay,
            isShowModalNetworkDifference: computed(() => networkDifferences.value.showModal),
            setShowModalNetworkDifferences,
            cartState,
            setCartState,
            changeCartItem
        } as const;
    },
    {
        persist: {
            storage: piniaPluginPersistedstate.localStorage(),
            serializer: {
                serialize: state => devalue.stringify(state),
                deserialize: state => devalue.parse(state)
            }
        }
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(
        useCartStore,
        import.meta.hot
    ));
}
